import {
  Logger,
  __name,
  __spreadProps,
  __spreadValues,
  getGlobalContext,
  getTimestamp
} from "./chunk-FLKB7XIS.js";

// src/common/data/bin.ts
var log = Logger("bin");
function _encodeUtf8Polyfill(str) {
  const encodedString = unescape(encodeURIComponent(str));
  const len = encodedString.length;
  const buf = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    buf[i] = encodedString.codePointAt(i) || 0;
  }
  return buf;
}
__name(_encodeUtf8Polyfill, "_encodeUtf8Polyfill");
function _decodeUtf8Polyfill(buf) {
  let remainingLen = buf.length;
  let encodedString = "";
  let bufPos = 0;
  while (remainingLen > 0) {
    const nextLen = remainingLen < 1e4 ? remainingLen : 1e4;
    const bytes = buf.subarray(bufPos, bufPos + nextLen);
    bufPos += nextLen;
    encodedString += String.fromCodePoint.apply(null, bytes);
    remainingLen -= nextLen;
  }
  return decodeURIComponent(escape(encodedString));
}
__name(_decodeUtf8Polyfill, "_decodeUtf8Polyfill");
var _textEncoder;
function stringToUInt8Array(text) {
  if (_textEncoder == null) {
    _textEncoder = _encodeUtf8Polyfill;
    if (typeof TextEncoder !== "undefined") {
      const encoder = new TextEncoder();
      _textEncoder = /* @__PURE__ */ __name((data) => encoder.encode(data), "_textEncoder");
    }
  }
  return _textEncoder(text.normalize("NFC"));
}
__name(stringToUInt8Array, "stringToUInt8Array");
var _textDecoder;
function Uint8ArrayToString(bin) {
  if (_textDecoder == null) {
    _textDecoder = _decodeUtf8Polyfill;
    if (typeof TextDecoder !== "undefined") {
      const decoder = new TextDecoder("utf-8", { ignoreBOM: true });
      _textDecoder = /* @__PURE__ */ __name((data) => decoder.decode(data), "_textDecoder");
    }
  }
  return _textDecoder(bin).normalize("NFC");
}
__name(Uint8ArrayToString, "Uint8ArrayToString");
function toUint8Array(data) {
  if (data instanceof ArrayBuffer)
    return new Uint8Array(data);
  if (typeof data === "string")
    return stringToUInt8Array(data);
  if (data.length)
    return new Uint8Array(data);
  return data;
}
__name(toUint8Array, "toUint8Array");
function toHex(bin) {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(toUint8Array(bin)).toString("hex");
  }
  const h = "0123456789abcdef";
  let s = "";
  for (const v of [...toUint8Array(bin)]) {
    s += h[v >> 4] + h[v & 15];
  }
  return s;
}
__name(toHex, "toHex");
function toBase64(bin) {
  const bytes = toUint8Array(bin);
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64");
  }
  let s = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    s += String.fromCharCode(bytes[i]);
  }
  return btoa(s);
}
__name(toBase64, "toBase64");
function toBase64Url(bin) {
  const bytes = toUint8Array(bin);
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64url");
  }
  let s = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    s += String.fromCharCode(bytes[i]);
  }
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_");
}
__name(toBase64Url, "toBase64Url");
function fromBase64(s) {
  if (typeof Buffer !== "undefined") {
    const buf = Buffer.from(s, "base64");
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  const a = atob(s);
  const bytes = new Uint8Array(a.length);
  for (let i = 0; i < a.length; i++) {
    bytes[i] = a.charCodeAt(i);
  }
  return bytes;
}
__name(fromBase64, "fromBase64");
function equalBinary(a, b) {
  if (a.byteLength !== b.byteLength)
    return false;
  const aa = toUint8Array(a);
  const bb = toUint8Array(b);
  for (let i = 0; i < aa.length; i++) {
    if (aa[i] !== bb[i]) {
      return false;
    }
  }
  return true;
}
__name(equalBinary, "equalBinary");
function jsonToUint8Array(json) {
  try {
    return stringToUInt8Array(JSON.stringify(json));
  } catch (err) {
    log.warn("jsonToUint8Array", json);
    throw err;
  }
}
__name(jsonToUint8Array, "jsonToUint8Array");
function Uint8ArrayToJson(data) {
  try {
    return JSON.parse(Uint8ArrayToString(data));
  } catch (err) {
    log.warn("Uint8ArrayToJson", data);
    throw err;
  }
}
__name(Uint8ArrayToJson, "Uint8ArrayToJson");

// src/common/crypto.ts
function randomUint8Array(length = 16) {
  let randomBytes = new Uint8Array(length);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(randomBytes);
  } else {
    for (let i = 0; i < length; i++) {
      randomBytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return randomBytes;
}
__name(randomUint8Array, "randomUint8Array");
var DEFAULT_HASH_ALG = "SHA-256";
var DEFAULT_CRYPTO_ALG = "AES-GCM";
var DEFAULT_DERIVE_ALG = "PBKDF2";
async function digest(message, algorithm = DEFAULT_HASH_ALG) {
  return toUint8Array(await crypto.subtle.digest(algorithm, toUint8Array(message)));
}
__name(digest, "digest");
async function deriveKeyPbkdf2(secret, opt = {}) {
  var _a;
  const secretBuffer = toUint8Array(secret);
  const keyMaterial = await crypto.subtle.importKey("raw", secretBuffer, DEFAULT_DERIVE_ALG, false, ["deriveKey"]);
  return await crypto.subtle.deriveKey({
    name: DEFAULT_DERIVE_ALG,
    salt: opt.salt ? toUint8Array(opt.salt) : new Uint8Array(0),
    iterations: (_a = opt.iterations) != null ? _a : 1e5,
    hash: DEFAULT_HASH_ALG
  }, keyMaterial, {
    name: DEFAULT_CRYPTO_ALG,
    length: 256
  }, true, ["encrypt", "decrypt"]);
}
__name(deriveKeyPbkdf2, "deriveKeyPbkdf2");
var MAGIC_ID = new Uint8Array([1, 1]);
async function encrypt(data, key) {
  const iv = randomUint8Array(12);
  const cipher = await crypto.subtle.encrypt({ name: DEFAULT_CRYPTO_ALG, iv }, key, data);
  const binCypher = new Uint8Array(cipher);
  const bufferLength = MAGIC_ID.length + iv.length + binCypher.length;
  const buffer = new Uint8Array(bufferLength);
  let pos = 0;
  buffer.set(MAGIC_ID, pos);
  pos += MAGIC_ID.length;
  buffer.set(iv, pos);
  pos += iv.length;
  buffer.set(binCypher, pos);
  return buffer;
}
__name(encrypt, "encrypt");
async function decrypt(data, key) {
  let magic = data.subarray(0, 2);
  if (!equalBinary(magic, MAGIC_ID)) {
    return Promise.reject(`Unknown magic ${magic}`);
  }
  let iv = data.subarray(2, 2 + 12);
  let cipher = data.subarray(2 + 12, data.length);
  const plain = await crypto.subtle.decrypt({ name: DEFAULT_CRYPTO_ALG, iv }, key, cipher);
  return new Uint8Array(plain);
}
__name(decrypt, "decrypt");

// src/common/csv.ts
var separator = ",";
var preventCast = false;
var ignoreNullOrUndefined = true;
function filterFloat(value) {
  if (/^([-+])?([0-9]+(\.[0-9]+)?|Infinity)$/.test(value)) {
    return Number(value);
  }
  return NaN;
}
__name(filterFloat, "filterFloat");
function escape2(field) {
  if (ignoreNullOrUndefined && field == void 0) {
    return "";
  }
  if (preventCast) {
    return '="' + String(field).replace(/"/g, '""') + '"';
  }
  if (!isNaN(filterFloat(field)) && isFinite(field)) {
    return parseFloat(field);
  }
  return '"' + String(field).replace(/"/g, '""') + '"';
}
__name(escape2, "escape");
function csv(data, headerRow) {
  let body = "";
  if (headerRow) {
    body = headerRow.join(separator) + "\r\n";
  }
  for (let i = 0; i < data.length; i++) {
    body += data[i].map(escape2).join(separator) + "\r\n";
  }
  return body;
}
__name(csv, "csv");

// src/common/data/orderby.ts
function parseOrderby(value = "") {
  let [field = "", orderby = "asc"] = value.split(" ");
  orderby = orderby.toLowerCase();
  return {
    field,
    orderby,
    asc: orderby !== "desc",
    desc: orderby === "desc"
  };
}
__name(parseOrderby, "parseOrderby");
function composeOrderby(field, asc = true) {
  return `${field} ${asc ? "asc" : "desc"}`;
}
__name(composeOrderby, "composeOrderby");
function cmp(a, b, asc = true) {
  const aa = a || 0;
  const bb = b || 0;
  return aa > bb ? asc ? 1 : -1 : aa < bb ? asc ? -1 : 1 : 0;
}
__name(cmp, "cmp");
function sortedOrderby(values, ...orderby) {
  if (orderby.length > 0) {
    let orderByList = orderby.map(parseOrderby);
    let sortValues = Array.from(values);
    sortValues.sort((a, b) => {
      for (let { field, asc } of orderByList) {
        const result = cmp(a[field], b[field], asc);
        if (result !== 0)
          return result;
      }
      return 0;
    });
    return sortValues;
  }
  return values;
}
__name(sortedOrderby, "sortedOrderby");

// src/common/data/array.ts
function arrayUnique(x) {
  return x.filter((n, index) => x.indexOf(n) === index);
}
__name(arrayUnique, "arrayUnique");
function arrayMinus(x, y) {
  return arrayUnique(x.filter((n) => !y.includes(n)));
}
__name(arrayMinus, "arrayMinus");
function arrayUnion(...a) {
  return arrayUnique(a.reduce((acc = [], value) => acc.concat(value), []));
}
__name(arrayUnion, "arrayUnion");
function arrayFlatten(...list) {
  return list.flat(Infinity);
}
__name(arrayFlatten, "arrayFlatten");
function arrayIntersection(x, y) {
  return arrayUnique(x).filter((n) => y.includes(n));
}
__name(arrayIntersection, "arrayIntersection");
function arraySymmetricDifference(x, y) {
  return arrayMinus(arrayUnion(x, y), arrayIntersection(x, y));
}
__name(arraySymmetricDifference, "arraySymmetricDifference");
function arrayRemoveElement(arr, el) {
  if (arr && Array.isArray(arr)) {
    let index;
    while ((index = arr.indexOf(el)) !== -1) {
      arr.splice(index, 1);
    }
    return arr;
  }
  return [];
}
__name(arrayRemoveElement, "arrayRemoveElement");
function arraySetElement(arr, el) {
  if (!arr.includes(el))
    arr.push(el);
  return arr;
}
__name(arraySetElement, "arraySetElement");
function arrayFilterInPlace(array, fn) {
  array.splice(0, array.length, ...array.filter(fn));
  return array;
}
__name(arrayFilterInPlace, "arrayFilterInPlace");
function arrayToggleInPlace(array, el) {
  const index = array.findIndex((e) => e === el);
  if (index >= 0)
    array.splice(index, 1);
  else
    array.push(el);
  return array;
}
__name(arrayToggleInPlace, "arrayToggleInPlace");
function arrayEmptyInPlace(array) {
  array.splice(0, array.length);
  return array;
}
__name(arrayEmptyInPlace, "arrayEmptyInPlace");
function arraySorted(arr, cond = cmp) {
  return Array.from(arr).sort(cond);
}
__name(arraySorted, "arraySorted");
function arraySortedNumbers(arr) {
  return arraySorted(arr, (l, r) => l - r);
}
__name(arraySortedNumbers, "arraySortedNumbers");
function arrayIsEqual(array1, array2) {
  return array1.length === array2.length && array1.every((value, index) => value === array2[index]);
}
__name(arrayIsEqual, "arrayIsEqual");
function arrayShuffleInPlace(array) {
  array.sort(() => Math.random() > 0.5 ? 1 : -1);
  return array;
}
__name(arrayShuffleInPlace, "arrayShuffleInPlace");
function arrayShuffle(array) {
  return arrayShuffleInPlace(Array.from(array));
}
__name(arrayShuffle, "arrayShuffle");
function arrayShuffleForce(array) {
  while (array.length > 1) {
    const copy = Array.from(array);
    arrayShuffleInPlace(copy);
    if (!arrayIsEqual(array, copy))
      return copy;
  }
  return array;
}
__name(arrayShuffleForce, "arrayShuffleForce");
function arrayRandomElement(array) {
  return array[Math.floor(Math.random() * array.length)];
}
__name(arrayRandomElement, "arrayRandomElement");
function arrayMax(...array) {
  return arrayFlatten(array).reduce((acc, value) => acc != null ? value > acc ? value : acc : value, void 0);
}
__name(arrayMax, "arrayMax");
function arrayMin(...array) {
  return arrayFlatten(array).reduce((acc, value) => acc != null ? value < acc ? value : acc : value, void 0);
}
__name(arrayMin, "arrayMin");
function createArray(size2 = 0, item) {
  if (size2 <= 0)
    return [];
  let arr = new Array(size2);
  for (let i = 0; i < size2; i++) {
    arr[i] = item instanceof Function ? item(i) : item;
  }
  return arr;
}
__name(createArray, "createArray");

// src/common/data/basex.ts
var log2 = Logger("zeed:basex");
var alphabets = {
  "2": "01",
  "8": "01234567",
  "11": "0123456789a",
  "16": "0123456789abcdef",
  "32": "0123456789ABCDEFGHJKMNPQRSTVWXYZ",
  "32-rfc": "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567",
  "32-hex": "0123456789ABCDEFGHIJKLMNOPQRSTUV",
  "32-zbase": "ybndrfg8ejkmcpqxot1uwisza345h769",
  "36": "0123456789abcdefghijklmnopqrstuvwxyz",
  "58": "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz",
  "62": "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
  "64": "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/",
  "64-url": "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_",
  "66": "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_.!~",
  "85": "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!#$%&()*+-;<=>?@^_`{|}~"
};
function useBase(alphaOrBase) {
  let ALPHABET;
  if (typeof alphaOrBase === "string") {
    ALPHABET = alphaOrBase;
  } else {
    ALPHABET = alphabets[alphaOrBase.toString()];
    if (ALPHABET == null)
      throw new Error(`Unknown base ${alphaOrBase}`);
  }
  if (ALPHABET.length >= 255)
    throw new TypeError("Alphabet too long");
  const BASE_MAP = new Uint8Array(256);
  for (let j = 0; j < BASE_MAP.length; j++) {
    BASE_MAP[j] = 255;
  }
  for (let i = 0; i < ALPHABET.length; i++) {
    const x = ALPHABET.charAt(i);
    const xc = x.charCodeAt(0);
    if (BASE_MAP[xc] !== 255)
      throw new TypeError(x + " is ambiguous");
    BASE_MAP[xc] = i;
  }
  const BASE = ALPHABET.length;
  const LEADER = ALPHABET.charAt(0);
  const FACTOR = Math.log(BASE) / Math.log(256);
  const iFACTOR = Math.log(256) / Math.log(BASE);
  function encode(source, padToLength = -1) {
    let data = toUint8Array(source);
    if (data.byteLength === 0)
      return "";
    let length = 0;
    let pbegin = 0;
    const pend = data.byteLength;
    while (pbegin !== pend && data[pbegin] === 0)
      pbegin++;
    const size2 = (pend - pbegin) * iFACTOR + 1 >>> 0;
    const dataEncoded = new Uint8Array(size2);
    while (pbegin !== pend) {
      let carry = data[pbegin];
      let i = 0;
      for (let it1 = size2 - 1; (carry !== 0 || i < length) && it1 !== -1; it1--, i++) {
        carry += 256 * dataEncoded[it1] >>> 0;
        dataEncoded[it1] = carry % BASE >>> 0;
        carry = carry / BASE >>> 0;
      }
      if (carry !== 0) {
        log2.warn("Non-zero carry", data, padToLength, i, size2);
        throw new Error("Non-zero carry");
      }
      length = i;
      pbegin++;
    }
    let it2 = size2 - length;
    while (it2 !== size2 && dataEncoded[it2] === 0) {
      it2++;
    }
    let str = "";
    for (; it2 < size2; ++it2)
      str += ALPHABET.charAt(dataEncoded[it2]);
    if (padToLength > 0) {
      return str.padStart(padToLength, LEADER);
    }
    return str;
  }
  __name(encode, "encode");
  function decode(source, padToLength = -1) {
    if (typeof source !== "string")
      throw new TypeError("Expected String");
    if (source.length === 0)
      return new Uint8Array();
    source = source.replace(/\s+/gi, "");
    let psz = 0;
    let length = 0;
    while (source[psz] === LEADER) {
      psz++;
    }
    const size2 = (source.length - psz) * FACTOR + 1 >>> 0;
    const dataDecoded = new Uint8Array(size2);
    while (source[psz]) {
      let carry = BASE_MAP[source.charCodeAt(psz)];
      if (carry === 255)
        throw new Error(`Unsupported character "${source[psz]}"`);
      let i = 0;
      for (let it3 = size2 - 1; (carry !== 0 || i < length) && it3 !== -1; it3--, i++) {
        carry += BASE * dataDecoded[it3] >>> 0;
        dataDecoded[it3] = carry % 256 >>> 0;
        carry = carry / 256 >>> 0;
      }
      if (carry !== 0)
        throw new Error("Non-zero carry");
      length = i;
      psz++;
    }
    let it4 = size2 - length;
    while (it4 !== size2 && dataDecoded[it4] === 0) {
      it4++;
    }
    if (padToLength > 0) {
      return new Uint8Array([
        ...new Uint8Array(padToLength - dataDecoded.length + it4),
        ...dataDecoded.slice(it4)
      ]);
    }
    return dataDecoded.slice(it4);
  }
  __name(decode, "decode");
  return {
    encode,
    decode
  };
}
__name(useBase, "useBase");
var { encode: encodeBase16, decode: decodeBase16 } = useBase(16);
var { encode: encodeBase32, decode: decodeBase32 } = useBase(32);
var { encode: encodeBase58, decode: decodeBase58 } = useBase(58);
var { encode: encodeBase62, decode: decodeBase62 } = useBase(62);
var { encode: encodeBase64, decode: decodeBase64 } = useBase(62);

// src/common/data/camelcase.ts
var toCamelCase = /* @__PURE__ */ __name((s) => {
  if (s.length > 0) {
    if (/^[A-Z0-9_\-\ ]*$/g.test(s)) {
      s = s.toLowerCase();
    }
    s = s.replace(/^[-_\ ]+/gi, "").replace(/[-_\ ]+$/gi, "").replace(/[-_\ ]+([a-z0-9])/gi, ($0, $1) => $1.toUpperCase());
    s = s[0].toLowerCase() + s.substring(1);
  }
  return s;
}, "toCamelCase");
function toCapitalize(s) {
  return s.charAt(0).toUpperCase() + s.toLowerCase().slice(1);
}
__name(toCapitalize, "toCapitalize");
function toCapitalizeWords(s) {
  return s.replace(/\w\S*/g, toCapitalize);
}
__name(toCapitalizeWords, "toCapitalizeWords");
function fromCamelCase(str, separator2 = "-") {
  separator2 = typeof separator2 === "undefined" ? "_" : separator2;
  return str.replace(/([a-z\d])([A-Z])/g, "$1" + separator2 + "$2").replace(/([A-Z]+)([A-Z][a-z\d]+)/g, "$1" + separator2 + "$2").toLowerCase();
}
__name(fromCamelCase, "fromCamelCase");

// src/common/data/json.ts
function serializer(replacer, cycleReplacer) {
  var stack = [], keys = [];
  if (cycleReplacer == null)
    cycleReplacer = /* @__PURE__ */ __name(function(key, value) {
      if (stack[0] === value)
        return "[Circular ~]";
      return "[Circular ~." + keys.slice(0, stack.indexOf(value)).join(".") + "]";
    }, "cycleReplacer");
  return function(key, value) {
    if (stack.length > 0) {
      var thisPos = stack.indexOf(this);
      ~thisPos ? stack.splice(thisPos + 1) : stack.push(this);
      ~thisPos ? keys.splice(thisPos, Infinity, key) : keys.push(key);
      if (~stack.indexOf(value))
        value = cycleReplacer == null ? void 0 : cycleReplacer.call(this, key, value);
    } else
      stack.push(value);
    return replacer == null ? value : replacer.call(this, key, value);
  };
}
__name(serializer, "serializer");
function jsonStringify(obj, replacer, spaces, cycleReplacer) {
  return JSON.stringify(obj, serializer(replacer, cycleReplacer), spaces);
}
__name(jsonStringify, "jsonStringify");

// src/common/data/convert.ts
var TRUE_VALUES_LIST = ["1", "true", "yes", "y", "on"];
function stringToBoolean(value, defaultValue = false) {
  if (value == null || typeof value !== "string")
    return defaultValue;
  return TRUE_VALUES_LIST.includes(String(value).trim().toLowerCase());
}
__name(stringToBoolean, "stringToBoolean");
function stringToInteger(value, defaultValue = 0) {
  var _a;
  if (value == null || typeof value !== "string")
    return defaultValue;
  return (_a = parseInt(value.trim(), 10)) != null ? _a : defaultValue;
}
__name(stringToInteger, "stringToInteger");
function stringToFloat(value, defaultValue = 0) {
  var _a;
  if (value == null || typeof value !== "string")
    return defaultValue;
  return (_a = parseFloat(value.trim())) != null ? _a : defaultValue;
}
__name(stringToFloat, "stringToFloat");
function valueToBoolean(value, defaultValue = false) {
  if (value == null)
    return defaultValue;
  if (typeof value === "boolean")
    return value;
  if (typeof value === "number")
    return value !== 0;
  return TRUE_VALUES_LIST.includes(String(value).trim().toLowerCase());
}
__name(valueToBoolean, "valueToBoolean");
function valueToInteger(value, defaultValue = 0) {
  var _a;
  if (value == null)
    return defaultValue;
  if (typeof value === "boolean")
    return value ? 1 : 0;
  if (typeof value === "number")
    return Math.floor(value);
  return (_a = parseInt(String(value).trim(), 10)) != null ? _a : defaultValue;
}
__name(valueToInteger, "valueToInteger");
function valueToFloat(value, defaultValue = 0) {
  var _a;
  if (value == null)
    return defaultValue;
  if (typeof value === "boolean")
    return value ? 1 : 0;
  if (typeof value === "number")
    return Math.floor(value);
  return (_a = parseFloat(String(value).trim())) != null ? _a : defaultValue;
}
__name(valueToFloat, "valueToFloat");
function valueToString(value, defaultValue = "") {
  var _a;
  if (value == null)
    return defaultValue;
  return (_a = String(value)) != null ? _a : defaultValue;
}
__name(valueToString, "valueToString");
var toFloat = valueToFloat;
var toInt = valueToInteger;
var toString = valueToString;
var toBool = valueToBoolean;
function formatMessages(messages2, opt = {}) {
  const { trace = true, pretty = true } = opt;
  return messages2.map((obj) => {
    if (obj && typeof obj === "object") {
      if (obj instanceof Error) {
        if (!trace) {
          return `${obj.name || "Error"}: ${obj.message}`;
        }
        return `${obj.name || "Error"}: ${obj.message}
${obj.stack}`;
      }
      return pretty ? jsonStringify(obj, null, 2) : jsonStringify(obj);
    }
    return String(obj);
  });
}
__name(formatMessages, "formatMessages");
function renderMessages(messages2, opt = {}) {
  return formatMessages(messages2, opt).join(" ");
}
__name(renderMessages, "renderMessages");
function fixBrokenUth8String(brokenString) {
  try {
    return decodeURIComponent(escape(brokenString));
  } catch (e) {
  }
  return brokenString;
}
__name(fixBrokenUth8String, "fixBrokenUth8String");

// src/common/data/currency.ts
var defaults = {
  symbol: "$",
  separator: ",",
  decimal: ".",
  errorOnInvalid: false,
  precision: 2,
  pattern: "!#",
  negativePattern: "-!#",
  format,
  fromCents: false
};
var round = /* @__PURE__ */ __name((v) => Math.round(v), "round");
var pow = /* @__PURE__ */ __name((p) => Math.pow(10, p), "pow");
var rounding = /* @__PURE__ */ __name((value, increment) => round(value / increment) * increment, "rounding");
var groupRegex = /(\d)(?=(\d{3})+\b)/g;
var vedicRegex = /(\d)(?=(\d\d)+\d\b)/g;
function currency(value, opts = {}) {
  return new Currency(value, opts);
}
__name(currency, "currency");
var _Currency = class {
  constructor(value, opts = {}) {
    var _a;
    let settings = Object.assign({}, defaults, opts);
    let precision = pow((_a = settings.precision) != null ? _a : 2);
    let v = parse(value, settings);
    this.intValue = v;
    this.value = v / precision;
    settings.increment = settings.increment || 1 / precision;
    if (settings.useVedic) {
      settings.groups = vedicRegex;
    } else {
      settings.groups = groupRegex;
    }
    this._settings = settings;
    this._precision = precision;
  }
  add(number) {
    let { intValue, _settings, _precision } = this;
    return currency((intValue += parse(number, _settings)) / (_settings.fromCents ? 1 : _precision), _settings);
  }
  subtract(number) {
    let { intValue, _settings, _precision } = this;
    return currency((intValue -= parse(number, _settings)) / (_settings.fromCents ? 1 : _precision), _settings);
  }
  multiply(number) {
    let { intValue, _settings, _precision } = this;
    return currency((intValue *= number) / (_settings.fromCents ? 1 : pow(_precision)), _settings);
  }
  divide(number) {
    let { intValue, _settings } = this;
    return currency(intValue /= parse(number, _settings, false), _settings);
  }
  distribute(count) {
    let { intValue, _precision, _settings } = this, distribution = [], split = Math[intValue >= 0 ? "floor" : "ceil"](intValue / count), pennies = Math.abs(intValue - split * count), precision = _settings.fromCents ? 1 : _precision;
    for (; count !== 0; count--) {
      let item = currency(split / precision, _settings);
      pennies-- > 0 && (item = item[intValue >= 0 ? "add" : "subtract"](1 / precision));
      distribution.push(item);
    }
    return distribution;
  }
  dollars() {
    return ~~this.value;
  }
  cents() {
    let { intValue, _precision } = this;
    return ~~(intValue % _precision);
  }
  format(options) {
    let { _settings } = this;
    if (typeof options === "function") {
      return options(this, _settings);
    }
    return _settings.format(this, Object.assign({}, _settings, options));
  }
  toString() {
    let { intValue, _precision, _settings } = this;
    return rounding(intValue / _precision, _settings.increment).toFixed(_settings.precision);
  }
  toJSON() {
    return this.value;
  }
  static sum(...array) {
    return arrayFlatten(array).reduce((acc, value) => currency(acc).add(value), this.zero);
  }
  static avg(...array) {
    let arr = arrayFlatten(array);
    return arr.reduce((acc, value) => currency(acc).add(value), this.zero).divide(arr.length);
  }
};
var Currency = _Currency;
__name(Currency, "Currency");
Currency.zero = new _Currency(0);
Currency.one = new _Currency(1);
function parse(value, opts, useRounding = true) {
  let v = 0, { decimal, errorOnInvalid, precision: decimals, fromCents } = opts, precision = pow(decimals), isNumber = typeof value === "number";
  if (value instanceof Currency && fromCents) {
    return value.intValue;
  }
  if (isNumber || value instanceof Currency) {
    v = value instanceof Currency ? value.value : value;
  } else if (typeof value === "string") {
    let regex = new RegExp("[^-\\d" + decimal + "]", "g"), decimalString = new RegExp("\\" + decimal, "g");
    v = value.replace(/\((.*)\)/, "-$1").replace(regex, "").replace(decimalString, ".");
    v = v || 0;
  } else {
    if (errorOnInvalid) {
      throw Error("Invalid Input");
    }
    v = 0;
  }
  if (!fromCents) {
    v *= precision;
    v = v.toFixed(4);
  }
  return useRounding ? round(v) : v;
}
__name(parse, "parse");
function format(currency2, settings) {
  let { pattern: pattern2, negativePattern, symbol, separator: separator2, decimal, groups } = settings, split = ("" + currency2).replace(/^-/, "").split("."), dollars = split[0], cents = split[1];
  return (currency2.value >= 0 ? pattern2 : negativePattern).replace("!", symbol).replace("#", dollars.replace(groups, "$1" + separator2) + (cents ? decimal + cents : ""));
}
__name(format, "format");
var isHalf = /* @__PURE__ */ __name((value) => Math.abs(value) % 1 === 0.5, "isHalf");
var isEven = /* @__PURE__ */ __name((value) => value % 2 === 0, "isEven");
var roundUp = /* @__PURE__ */ __name((value) => Math.ceil(value), "roundUp");
var roundDown = /* @__PURE__ */ __name((value) => Math.floor(value), "roundDown");
var roundHalfUp = /* @__PURE__ */ __name((value) => Math.round(value), "roundHalfUp");
var roundHalfOdd = /* @__PURE__ */ __name((value) => {
  const rounded = Math.round(value);
  if (!isHalf(value)) {
    return rounded;
  }
  return isEven(rounded) ? rounded - 1 : rounded;
}, "roundHalfOdd");
var roundHalfAwayFromZero = /* @__PURE__ */ __name((value) => {
  return isHalf(value) ? Math.sign(value) * Math.ceil(Math.abs(value)) : Math.round(value);
}, "roundHalfAwayFromZero");
var roundHalfDown = /* @__PURE__ */ __name((value) => isHalf(value) ? Math.floor(value) : Math.round(value), "roundHalfDown");
var roundHalfEven = /* @__PURE__ */ __name((value) => {
  const rounded = Math.round(value);
  if (!isHalf(value)) {
    return rounded;
  }
  return isEven(rounded) ? rounded : rounded - 1;
}, "roundHalfEven");
var roundHalfTowardsZero = /* @__PURE__ */ __name((value) => isHalf(value) ? Math.sign(value) * Math.floor(Math.abs(value)) : Math.round(value), "roundHalfTowardsZero");

// src/common/promise.ts
var { warn } = Logger("zeed:promise");
async function sleep(milliSeconds) {
  return new Promise((resolve) => setTimeout(resolve, milliSeconds));
}
__name(sleep, "sleep");
async function immediate() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}
__name(immediate, "immediate");
var timeoutReached = Symbol("timeout");
async function timeout(promise, milliSeconds, timeoutValue = timeoutReached) {
  return new Promise(async (resolve, reject) => {
    let done = false;
    const timeout2 = setTimeout(() => {
      done = true;
      resolve(timeoutValue);
    }, milliSeconds);
    try {
      let result = await promise;
      clearTimeout(timeout2);
      if (!done)
        resolve(result);
    } catch (err) {
      clearTimeout(timeout2);
      if (!done)
        reject(err);
    }
  });
}
__name(timeout, "timeout");
var timoutError = new Error("Timeout reached");
function isTimeout(value) {
  return value === timeoutReached || value === timoutError;
}
__name(isTimeout, "isTimeout");
async function tryTimeout(promise, milliSeconds) {
  if (milliSeconds <= 0) {
    return await promise;
  }
  return new Promise(async (resolve, reject) => {
    let done = false;
    const timeout2 = setTimeout(() => {
      done = true;
      reject(timoutError);
    }, milliSeconds);
    try {
      let result = await promise;
      clearTimeout(timeout2);
      if (!done)
        resolve(result);
    } catch (err) {
      clearTimeout(timeout2);
      if (!done)
        reject(err);
    }
  });
}
__name(tryTimeout, "tryTimeout");
function waitOn(obj, event, timeoutMS = 1e3) {
  return new Promise((resolve, reject) => {
    let fn = /* @__PURE__ */ __name((value) => {
      if (timer) {
        clearTimeout(timer);
        done();
        resolve(value);
      }
    }, "fn");
    let done = /* @__PURE__ */ __name(() => {
      timer = null;
      if (obj.off) {
        obj.off(event, fn);
      } else if (obj.removeEventListener) {
        obj.removeEventListener(event, fn);
      } else {
        warn("No remove listener method found for", obj, event);
      }
    }, "done");
    let timer = setTimeout(() => {
      done();
      reject(new Error(`Did not response in time`));
    }, timeoutMS);
    if (obj.on) {
      obj.on(event, fn);
    } else if (obj.addEventListener) {
      obj.addEventListener(event, fn);
    } else {
      warn("No listener method found for", obj);
    }
  });
}
__name(waitOn, "waitOn");
function isPromise(value) {
  return Boolean(value && (value instanceof Promise || typeof value.then === "function"));
}
__name(isPromise, "isPromise");
function promisify(value) {
  return Promise.resolve(value);
}
__name(promisify, "promisify");

// src/common/data/day.ts
var DAY_MS = 1e3 * 60 * 60 * 24;
var Day = class {
  constructor(days) {
    var _a;
    if (typeof days === "number") {
      this.days = days;
      return;
    }
    if (days != null) {
      days = (_a = Day.from(days)) == null ? void 0 : _a.days;
    }
    if (days == null) {
      const date = new Date();
      this.days = date.getFullYear() * 1e4 + (date.getMonth() + 1) * 100 + date.getDate();
    } else {
      this.days = days;
    }
  }
  static fromNumber(n) {
    return new Day(n);
  }
  static fromString(dateString) {
    return new Day(+dateString.replace(/[^0-9]/g, ""));
  }
  static fromDate(date, gmt = false) {
    return gmt ? Day.fromString(date.toISOString().substr(0, 10)) : new Day(date.getFullYear() * 1e4 + (date.getMonth() + 1) * 100 + date.getDate());
  }
  static fromDateGMT(date) {
    return Day.fromDate(date, true);
  }
  static from(value, gmt = false) {
    if (typeof value === "number") {
      return new Day(value);
    } else if (typeof value === "string") {
      return Day.fromString(value);
    } else if (value instanceof Date) {
      return Day.fromDate(value, gmt);
    } else if (value instanceof Day) {
      return value;
    }
  }
  toNumber() {
    return this.days;
  }
  toJson() {
    return this.days;
  }
  toString(sep = "-") {
    let baseString = String(this.days);
    return baseString.slice(0, 4) + sep + baseString.slice(4, 6) + sep + baseString.slice(6, 8);
  }
  toDate(gmt = false) {
    return gmt ? new Date(`${this.toString()}T00:00:00.000Z`) : new Date(this.days / 1e4, this.days / 100 % 100 - 1, this.days % 100);
  }
  toDateGMT() {
    return this.toDate(true);
  }
  dayOffset(offset) {
    return Day.fromDateGMT(new Date(this.toDateGMT().getTime() + offset * DAY_MS));
  }
  daysUntil(otherDay) {
    var _a;
    return Math.round((((_a = new Day(otherDay)) == null ? void 0 : _a.toDateGMT().getTime()) - this.toDateGMT().getTime()) / DAY_MS);
  }
  yesterday() {
    return this.dayOffset(-1);
  }
  tomorrow() {
    return this.dayOffset(1);
  }
};
__name(Day, "Day");
async function forEachDay(from, to, handler) {
  let start = Day.from(from);
  let end = Day.from(to);
  while (start && end && (start == null ? void 0 : start.days) <= (end == null ? void 0 : end.days)) {
    let result = handler(start);
    if (isPromise(result)) {
      await result;
    }
    start = start.dayOffset(1);
  }
}
__name(forEachDay, "forEachDay");
function today() {
  return new Day();
}
__name(today, "today");
function day(days) {
  return new Day(days);
}
__name(day, "day");

// src/common/data/html.ts
var escapeHTML = /* @__PURE__ */ __name((s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/'/g, "&apos;").replace(/"/g, "&quot;"), "escapeHTML");
var unescapeHTML = /* @__PURE__ */ __name((s) => s.replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&quot;/gi, '"').replace(/&apos;/gi, "'").replace(/&amp;/gi, "&"), "unescapeHTML");

// src/common/data/math.ts
function randomBoolean(bias = 0.25) {
  return Math.random() < bias;
}
__name(randomBoolean, "randomBoolean");
function randomInt(max = 100, min = 0) {
  return min + Math.floor(Math.random() * (max - min));
}
__name(randomInt, "randomInt");
function randomFloat(max = 100, min = 0) {
  return min + Math.random() * (max - min);
}
__name(randomFloat, "randomFloat");
function between(min, value, max) {
  return Math.max(min, Math.min(max, value));
}
__name(between, "between");

// src/common/data/path.ts
var MAX_FILENAME_LENGTH = 100;
var reControlChars = /[\u0000-\u001F\u0080-\u009F]/g;
var reRelativePath = /^\.+/;
var reTrailingPeriods = /\.+$/;
function filenameReservedRegex() {
  return /[<>:"/\\|?*\u0000-\u001F]/g;
}
__name(filenameReservedRegex, "filenameReservedRegex");
function windowsReservedNameRegex() {
  return /^(con|prn|aux|nul|com\d|lpt\d)$/i;
}
__name(windowsReservedNameRegex, "windowsReservedNameRegex");
function toValidFilename(string) {
  if (typeof string !== "string") {
    throw new TypeError("Expected a string");
  }
  const replacement = "_";
  if (filenameReservedRegex().test(replacement) && reControlChars.test(replacement)) {
    throw new Error("Replacement string cannot contain reserved filename characters");
  }
  string = string.replace(filenameReservedRegex(), replacement).replace(reControlChars, replacement).replace(reRelativePath, replacement).replace(reTrailingPeriods, "");
  string = windowsReservedNameRegex().test(string) ? string + replacement : string;
  return string.slice(0, MAX_FILENAME_LENGTH);
}
__name(toValidFilename, "toValidFilename");

// src/common/data/regexp.ts
var rxEscape = /[\\\-\[\]\/{}()*+?.^$|]/g;
function escapeRegExp(value) {
  if (!value)
    return "";
  if (value instanceof RegExp) {
    return value.source;
  }
  return value.replace(rxEscape, "\\$&");
}
__name(escapeRegExp, "escapeRegExp");

// src/common/data/sortable.ts
function startSortWeight(items) {
  return items.reduce((acc, item) => Math.min(acc, item.sort_weight || 0), 0) - 1 - Math.random();
}
__name(startSortWeight, "startSortWeight");
function endSortWeight(items) {
  return items.reduce((acc, item) => Math.max(acc, item.sort_weight || 0), 0) + 1 + Math.random();
}
__name(endSortWeight, "endSortWeight");
function moveSortWeight(newIndex, oldIndex, items) {
  let count = items.length;
  const moveLower = newIndex < oldIndex;
  if (count <= 0 || newIndex >= count - 1) {
    return endSortWeight(items);
  }
  if (newIndex <= 0) {
    return startSortWeight(items);
  }
  items = sortedItems([...items]);
  const step = moveLower ? -1 : 0;
  const lower = items[newIndex + step].sort_weight || 0;
  const upper = items[newIndex + step + 1].sort_weight || 0;
  const distance = upper - lower;
  if (distance === 0) {
    if (moveLower) {
      return startSortWeight(items);
    }
    return endSortWeight(items);
  }
  const middle = lower + distance / 2;
  const fuzzy = distance * 0.01 * (Math.random() - 0.5);
  return middle + fuzzy;
}
__name(moveSortWeight, "moveSortWeight");
function sortedItems(items) {
  items.sort((a, b) => (a.sort_weight || 0) - (b.sort_weight || 0));
  return items;
}
__name(sortedItems, "sortedItems");

// src/common/data/url.ts
var findURL = /((?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?)/gim;
function linkifyPlainText(text) {
  return text.split(findURL).map((part, i) => {
    const escapedPart = escapeHTML(part);
    return i % 2 ? `<a target="_blank" href="${escapedPart}">${toHumanReadableUrl(escapedPart)}</a>` : escapedPart;
  }).join("");
}
__name(linkifyPlainText, "linkifyPlainText");
function toHumanReadableUrl(url) {
  return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}
__name(toHumanReadableUrl, "toHumanReadableUrl");
function encodeQuery(data) {
  let pairs = [];
  for (let [key, value] of Object.entries(data)) {
    if (value != null) {
      if (!Array.isArray(value)) {
        value = [value];
      }
      for (let v of value) {
        if (v != null) {
          pairs.push(encodeURIComponent(key) + "=" + encodeURIComponent(v.toString() || ""));
        }
      }
    }
  }
  return pairs.join("&");
}
__name(encodeQuery, "encodeQuery");
function parseQuery(queryString) {
  let query = {};
  let pairs = (queryString[0] === "?" ? queryString.substr(1) : queryString).split("&");
  for (let i = 0; i < pairs.length; i++) {
    let pair = pairs[i].split("=");
    let key = decodeURIComponent(pair[0]);
    let value = decodeURIComponent(pair[1] || "");
    if (query[key] != null) {
      if (!Array.isArray(query[key])) {
        query[key] = [query[key]];
      }
      query[key].push(value);
    } else {
      query[key] = value;
    }
  }
  return query;
}
__name(parseQuery, "parseQuery");

// src/common/data/utils.ts
function ensureKey(obj, key, createFn) {
  let value = obj[key];
  if (value === void 0) {
    value = createFn(key, obj);
    obj[key] = value;
  }
  return value;
}
__name(ensureKey, "ensureKey");
async function ensureKeyAsync(obj, key, createFn) {
  let value = obj[key];
  if (value === void 0) {
    value = await createFn(key, obj);
    obj[key] = value;
  }
  return value;
}
__name(ensureKeyAsync, "ensureKeyAsync");
function size(obj) {
  if (obj != null) {
    if (obj.size != null) {
      return obj.size;
    }
    if (obj.length != null) {
      return obj.length;
    }
    return Object.keys(obj).length;
  }
  return 0;
}
__name(size, "size");
function last(array) {
  return array != null && array.length > 0 ? array[array.length - 1] : void 0;
}
__name(last, "last");
function empty(value) {
  try {
    if (value != null) {
      if (Array.isArray(value)) {
        return value.length <= 0;
      } else if (typeof value === "string") {
        return value.length <= 0;
      } else if ((value == null ? void 0 : value.size) != null) {
        return value.size <= 0;
      } else {
        return Object.keys(value).length <= 0;
      }
    }
  } catch (err) {
    console.error("Failed to check if empty for", value, err);
  }
  return true;
}
__name(empty, "empty");
function cloneObject(obj) {
  if (Object(obj) !== obj)
    return obj;
  return JSON.parse(JSON.stringify(obj));
}
__name(cloneObject, "cloneObject");
function cloneJsonObject(obj) {
  if (Object(obj) !== obj)
    return obj;
  return JSON.parse(JSON.stringify(obj));
}
__name(cloneJsonObject, "cloneJsonObject");

// src/common/dispose-defer.ts
async function callDisposer(disposable) {
  if (typeof disposable === "function") {
    await promisify(disposable());
  } else if (isPromise(disposable)) {
    await disposable;
  } else if (typeof disposable.dispose === "function") {
    await promisify(disposable.dispose());
  } else if (isPromise(disposable.dispose)) {
    await disposable.dispose;
  } else if (typeof disposable.cleanup === "function") {
    await promisify(disposable.cleanup());
  } else if (isPromise(disposable.cleanup)) {
    await disposable.cleanup;
  }
}
__name(callDisposer, "callDisposer");
function useDispose() {
  let tracked = [];
  const untrack = /* @__PURE__ */ __name(async (disposable) => {
    if (tracked.includes(disposable)) {
      arrayFilterInPlace(tracked, (el) => el !== disposable);
      await callDisposer(disposable);
    }
  }, "untrack");
  const dispose = /* @__PURE__ */ __name(async () => {
    while (tracked.length > 0) {
      await untrack(tracked[0]);
    }
  }, "dispose");
  const track = /* @__PURE__ */ __name((obj) => {
    tracked.unshift(obj);
    return () => untrack(obj);
  }, "track");
  return Object.assign(dispose, {
    track,
    add: track,
    untrack,
    dispose,
    exec: dispose,
    getSize() {
      return tracked.length;
    }
  });
}
__name(useDispose, "useDispose");
var useDisposer = useDispose;
function useDefer(config = {}) {
  const { mode = "fifo" } = config;
  let steps = [];
  const exec = /* @__PURE__ */ __name(async (expectSync = false) => {
    while (steps.length > 0) {
      let step = steps[0];
      arrayFilterInPlace(steps, (el) => el !== step);
      if (typeof step === "function") {
        let result = step();
        if (isPromise(result)) {
          if (expectSync)
            throw new Error(`Expected sync only function, but found async: ${step}`);
          await result;
        }
      } else if (isPromise(step)) {
        if (expectSync)
          throw new Error(`Expected sync only function, but found async: ${step}`);
        await step;
      } else {
        throw new Error(`Unhandled disposable: ${step}`);
      }
    }
  }, "exec");
  const add = /* @__PURE__ */ __name((obj) => {
    if (mode === "lifo") {
      steps.unshift(obj);
    } else {
      steps.push(obj);
    }
  }, "add");
  return Object.assign(exec, {
    add,
    exec,
    getSize() {
      return steps.length;
    }
  });
}
__name(useDefer, "useDefer");
function useTimeout(fn, timeout2 = 0) {
  let timeoutHandle = setTimeout(fn, timeout2);
  return () => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
      timeoutHandle = void 0;
    }
  };
}
__name(useTimeout, "useTimeout");
function useInterval(fn, interval) {
  let intervalHandle = setInterval(fn, interval);
  return () => {
    if (intervalHandle) {
      clearInterval(intervalHandle);
      intervalHandle = void 0;
    }
  };
}
__name(useInterval, "useInterval");
function useEventListener(emitter, eventName, fn, ...args) {
  if (emitter == null)
    return () => {
    };
  if (emitter.on) {
    emitter.on(eventName, fn, ...args);
  } else if (emitter.addEventListener) {
    emitter.addEventListener(eventName, fn, ...args);
  }
  return () => {
    if (emitter.off) {
      emitter.off(eventName, fn, ...args);
    } else if (emitter.removeEventListener) {
      emitter.removeEventListener(eventName, fn, ...args);
    }
  };
}
__name(useEventListener, "useEventListener");

// src/common/localhost.ts
function isLocalHost(hostname = ((_b) => (_b = ((_a) => (_a = globalThis == null ? void 0 : globalThis.location) == null ? void 0 : _a.hostname)()) != null ? _b : "")()) {
  return ["localhost", "127.0.0.1", "", "::1", "::"].includes(hostname) || hostname.startsWith("192.168.") || hostname.startsWith("10.0.") || hostname.endsWith(".local");
}
__name(isLocalHost, "isLocalHost");

// src/common/uuid.ts
var { encode: encode62, decode: decode62 } = useBase(62);
var { encode: encode32 } = useBase(32);
function uuidBytes() {
  return randomUint8Array(16);
}
__name(uuidBytes, "uuidBytes");
var uuid32bit = /* @__PURE__ */ __name(() => new Uint32Array(randomUint8Array(4))[0], "uuid32bit");
function uuid() {
  return encode62(uuidBytes(), 22);
}
__name(uuid, "uuid");
function uuidEncode(bytes) {
  return encode62(bytes, 22);
}
__name(uuidEncode, "uuidEncode");
function uuidDecode(uuid2) {
  return decode62(uuid2, 16);
}
__name(uuidDecode, "uuidDecode");
function uuidB32() {
  return encode32(uuidBytes(), 26);
}
__name(uuidB32, "uuidB32");
var _unameCounters = {};
function uname(name = "id") {
  if (_unameCounters[name] == null) {
    _unameCounters[name] = 0;
  }
  return `${name}-${_unameCounters[name]++}`;
}
__name(uname, "uname");
var _qid = 0;
function qid() {
  return `id-${_qid++}`;
}
__name(qid, "qid");
var pattern = "10000000-1000-4000-8000-100000000000";
var uuidv4 = /* @__PURE__ */ __name(() => pattern.replace(/[018]/g, (c) => (c ^ randomUint8Array(1)[0] & 15 >> c / 4).toString(16)), "uuidv4");
var ReferenceDateInMS = 16e11;
function longToByteArray(long) {
  var byteArray = new Uint8Array([0, 0, 0, 0, 0, 0]);
  const bytes = byteArray.length - 1;
  for (var index = 0; index < byteArray.length; index++) {
    var byte = long & 255;
    byteArray[bytes - index] = byte;
    long = (long - byte) / 256;
  }
  return byteArray;
}
__name(longToByteArray, "longToByteArray");
function suidBytes() {
  const ms = getTimestamp() - ReferenceDateInMS;
  return new Uint8Array([...longToByteArray(ms), ...randomUint8Array(10)]);
}
__name(suidBytes, "suidBytes");
function suid() {
  return encode62(suidBytes(), 22);
}
__name(suid, "suid");
function suidDate(id) {
  return suidBytesDate(decode62(id, 16));
}
__name(suidDate, "suidDate");
function suidBytesDate(id) {
  return new Date(ReferenceDateInMS + id.slice(0, 6).reduce((acc, byte) => {
    return acc * 256 + byte;
  }, 0));
}
__name(suidBytesDate, "suidBytesDate");

// src/common/msg/emitter.ts
var log3 = Logger("zeed:emitter");
var Emitter = class {
  constructor() {
    this.subscribers = {};
    this.subscribersOnAny = [];
    this.call = new Proxy({}, {
      get: (target, name) => (...args) => this.emit(name, ...args)
    });
  }
  async emit(event, ...args) {
    let ok = false;
    try {
      let subscribers = this.subscribers[event] || [];
      this.subscribersOnAny.forEach((fn) => fn(event, ...args));
      if (subscribers.length > 0) {
        let all = subscribers.map((fn) => {
          try {
            return promisify(fn(...args));
          } catch (err) {
            log3.warn("emit warning:", err);
          }
        });
        ok = true;
        await Promise.all(all);
      }
    } catch (err) {
      log3.error("emit exception", err);
    }
    return ok;
  }
  onAny(fn) {
    this.subscribersOnAny.push(fn);
  }
  on(event, listener) {
    let subscribers = this.subscribers[event] || [];
    subscribers.push(listener);
    this.subscribers[event] = subscribers;
    return () => {
      this.off(event, listener);
    };
  }
  onCall(handlers) {
    for (const [name, handler] of Object.entries(handlers)) {
      this.on(name, handler);
    }
  }
  once(event, listener) {
    const onceListener = /* @__PURE__ */ __name(async (...args) => {
      this.off(event, onceListener);
      return await promisify(listener(...args));
    }, "onceListener");
    this.on(event, onceListener);
    return () => {
      this.off(event, listener);
    };
  }
  off(event, listener) {
    this.subscribers[event] = (this.subscribers[event] || []).filter((f) => listener && f !== listener);
    return this;
  }
  removeAllListeners(event) {
    this.subscribers = {};
    return this;
  }
  dispose() {
    this.subscribers = {};
    this.subscribersOnAny = [];
  }
};
__name(Emitter, "Emitter");
function getGlobalEmitter() {
  let emitter = getGlobalContext().emitter;
  if (!emitter) {
    emitter = new Emitter();
    getGlobalContext().emitter = emitter;
  }
  return emitter;
}
__name(getGlobalEmitter, "getGlobalEmitter");
var messages = new Emitter();
function lazyListener(emitter, listenerKey) {
  const name = Math.round(Math.random() * 100);
  var events = [];
  var lazyResolve;
  const incoming = /* @__PURE__ */ __name((key, obj) => {
    let ev = { key, obj };
    events.push(ev);
    lazyResolve && lazyResolve();
  }, "incoming");
  if (listenerKey) {
    if (emitter.on) {
      emitter.on(listenerKey, (obj) => {
        incoming(listenerKey, obj);
      });
    } else if (emitter.addEventListener) {
      emitter.addEventListener(listenerKey, (obj) => {
        incoming(listenerKey, obj);
      });
    } else {
      log3.error(name, "Cannot listen to key");
    }
  } else {
    if (emitter.onAny) {
      emitter.onAny((key, obj) => {
        incoming(key, obj);
      });
    } else {
      log3.error(name, "cannot listen to all for", emitter);
    }
  }
  let on = /* @__PURE__ */ __name((key, skipUnmatched = true) => {
    return new Promise((resolve, reject) => {
      if (!key) {
        key = listenerKey;
        if (!key) {
          if (events.length) {
            key = events[0].key;
          }
        }
      }
      lazyResolve = /* @__PURE__ */ __name(() => {
        while (events.length > 0) {
          let ev = events.shift();
          if (ev.key === key) {
            lazyResolve = void 0;
            resolve(ev.obj);
          } else {
            if (skipUnmatched) {
              log3.warn(name, `Unhandled event ${key} with value: ${ev.obj}`);
              continue;
            }
            reject(`Expected ${key}, but found ${ev.key} with value=${ev.obj}`);
            log3.error(name, `Unhandled event ${key} with value: ${ev.obj}`);
          }
          break;
        }
      }, "lazyResolve");
      lazyResolve();
    });
  }, "on");
  return on;
}
__name(lazyListener, "lazyListener");

// src/common/msg/channel.ts
var Channel = class extends Emitter {
  constructor() {
    super(...arguments);
    this.id = uuid();
  }
  close() {
  }
};
__name(Channel, "Channel");
var LocalChannel = class extends Channel {
  constructor() {
    super(...arguments);
    this.isConnected = true;
  }
  postMessage(data) {
    var _a;
    (_a = this.other) == null ? void 0 : _a.emit("message", {
      data,
      origin: "local",
      lastEventId: uuid()
    });
  }
};
__name(LocalChannel, "LocalChannel");
function fakeWorkerPair() {
  let w1 = new LocalChannel();
  let w2 = new LocalChannel();
  w1.other = w2;
  w2.other = w1;
  return [w1, w2];
}
__name(fakeWorkerPair, "fakeWorkerPair");

// src/common/msg/encoder.ts
var NoopEncoder = class {
  async encode(data) {
    return data;
  }
  async decode(data) {
    return data;
  }
};
__name(NoopEncoder, "NoopEncoder");
var JsonEncoder = class {
  async encode(data) {
    return jsonToUint8Array(data);
  }
  async decode(data) {
    return Uint8ArrayToJson(data);
  }
};
__name(JsonEncoder, "JsonEncoder");
var CryptoEncoder = class {
  constructor(key) {
    this.key = key;
  }
  async encode(data) {
    const plain = jsonToUint8Array(data);
    return await encrypt(plain, this.key);
  }
  async decode(data) {
    const plain = await decrypt(data, this.key);
    return Uint8ArrayToJson(plain);
  }
};
__name(CryptoEncoder, "CryptoEncoder");

// src/common/msg/messages.ts
var createPromiseProxy = /* @__PURE__ */ __name((fn, opt, predefinedMethods = {}) => new Proxy(predefinedMethods, {
  get: (target, name) => {
    if (name in target)
      return target[name];
    return (...args) => fn(name, args, opt);
  }
}), "createPromiseProxy");
function useMessageHub(opt = {}) {
  let {
    name = uname("hub"),
    encoder = new JsonEncoder(),
    retryAfter = 1e3,
    ignoreUnhandled = true
  } = opt;
  const log8 = Logger(name);
  let handlers = {};
  let channel;
  let queue = [];
  let queueRetryTimer;
  let waitingForResponse = {};
  const dispose = /* @__PURE__ */ __name(() => {
    clearTimeout(queueRetryTimer);
  }, "dispose");
  const postNext = /* @__PURE__ */ __name(async () => {
    clearTimeout(queueRetryTimer);
    if (channel) {
      if (channel.isConnected) {
        while (queue.length) {
          let message = queue[0];
          try {
            channel.postMessage(await encoder.encode(message));
            queue.shift();
          } catch (err) {
            log8.warn("postMessage", err);
            break;
          }
        }
      }
      if (queue.length > 0 && retryAfter > 0) {
        queueRetryTimer = setTimeout(postNext, retryAfter);
      }
    }
  }, "postNext");
  const postMessage = /* @__PURE__ */ __name(async (message) => {
    log8("enqueue postMessage", message);
    queue.push(message);
    await postNext();
  }, "postMessage");
  const connect = /* @__PURE__ */ __name(async (newChannel) => {
    channel = newChannel;
    channel.on("connect", postNext);
    channel.on("message", async (msg) => {
      log8("onmessage", typeof msg);
      const { name: name2, args, id, result, error } = await encoder.decode(msg.data);
      if (name2) {
        log8(`name ${name2} id ${id}`);
        try {
          if (handlers[name2] == null) {
            throw new Error(`handler for ${name2} was not found`);
          }
          let result2 = handlers[name2](...args);
          if (isPromise(result2))
            result2 = await result2;
          log8(`result ${result2}`);
          if (id) {
            postMessage({ id, result: result2 });
          }
        } catch (error2) {
          let err = error2 instanceof Error ? error2 : new Error(valueToString(error2));
          log8.warn("execution error", err.name);
          postMessage({
            id,
            error: {
              message: err.message,
              stack: err.stack,
              name: err.name
            }
          });
        }
      } else if (id) {
        log8(`response for id=${id}: result=${result}, error=${error}`);
        if (waitingForResponse[id] == null) {
          if (result === void 0) {
            log8(`skip response for ${id}`);
          } else {
            log8.warn(`no response hook for ${id}`);
          }
        } else {
          const [resolve, reject] = waitingForResponse[id];
          if (resolve && reject) {
            delete waitingForResponse[id];
            if (error) {
              let err = new Error(error.message);
              err.stack = error.stack;
              err.name = error.name;
              log8.warn("reject", err.name);
              reject(err);
            } else {
              log8("resolve", result);
              resolve(result);
            }
          }
        }
      } else if (!ignoreUnhandled) {
        log8.warn("Unhandled message", msg);
      }
    });
    postNext();
  }, "connect");
  const fetchMessage = /* @__PURE__ */ __name(async (name2, args, opt2 = {}) => {
    const { timeout: timeout2 = 5e3 } = opt2;
    const id = uuid();
    postMessage({
      name: name2,
      args,
      id
    });
    return tryTimeout(new Promise((resolve, reject) => waitingForResponse[id] = [resolve, reject]), timeout2);
  }, "fetchMessage");
  if (opt.channel) {
    connect(opt.channel);
  }
  return {
    dispose,
    connect,
    listen(newHandlers) {
      Object.assign(handlers, newHandlers);
    },
    send() {
      return createPromiseProxy(fetchMessage, {}, {
        options(perCallopt) {
          return createPromiseProxy(fetchMessage, __spreadValues({}, perCallopt));
        }
      });
    }
  };
}
__name(useMessageHub, "useMessageHub");

// src/common/msg/pubsub.ts
var PubSub = class extends Emitter {
  constructor(opt) {
    super();
    this.publish = this.emit;
    this.subscribe = this.on;
    var _a;
    let { name, encoder = new JsonEncoder(), channel, debug = false } = opt;
    this.channel = channel;
    this.encoder = encoder;
    this.debug = debug;
    this.name = (_a = name != null ? name : this.channel.id) != null ? _a : uname("pubsub");
    this.log = Logger(`${this.shortId}`);
    if (this.debug) {
      this.channel.on("connect", () => {
        this.log("channel connected");
      });
      this.channel.on("disconnect", () => {
        this.log("channel disconnected");
      });
    }
    this.channel.on("message", async ({ data }) => {
      let info = await this.encoder.decode(data);
      if (this.debug)
        this.log(`channel message, event=${info == null ? void 0 : info.event}, info=`, info);
      else
        this.log(`channel message, event=${info == null ? void 0 : info.event}`);
      if (info) {
        const { event, args } = info;
        await this.emitSuper(event, ...args);
      }
    });
  }
  get shortId() {
    return this.name.substr(0, 6);
  }
  async emitSuper(event, ...args) {
    return await super.emit(event, ...args);
  }
  async emit(event, ...args) {
    try {
      if (this.debug)
        this.log(`emit(${event})`, event);
      else
        this.log(`emit(${event})`, args.length);
      if (!this.channel.isConnected) {
        this.log.warn("channel not connected");
        return false;
      }
      const data = await this.encoder.encode({ event, args });
      this.channel.postMessage(data);
      return true;
    } catch (err) {
      this.log.warn(`emit(${event})`, err);
    }
    return false;
  }
};
__name(PubSub, "PubSub");
function usePubSub(opt) {
  return new PubSub(opt);
}
__name(usePubSub, "usePubSub");

// src/common/mutex.ts
function useMutex() {
  let token = true;
  return (fn, elseFn) => {
    let executed = false;
    if (token) {
      token = false;
      try {
        fn();
        executed = true;
      } finally {
        token = true;
      }
    } else if (elseFn !== void 0) {
      elseFn();
    }
    return executed;
  };
}
__name(useMutex, "useMutex");
function useAsyncMutex() {
  let token = true;
  return async (fn, elseFn) => {
    let executed = false;
    if (token) {
      token = false;
      try {
        let result = fn();
        if (isPromise(result))
          await result;
        executed = true;
      } finally {
        token = true;
      }
    } else if (elseFn !== void 0) {
      let result = elseFn();
      if (isPromise(result))
        await result;
    }
    return executed;
  };
}
__name(useAsyncMutex, "useAsyncMutex");

// src/common/network.ts
var log4 = Logger("network");
var defaultOptions = {
  cache: "no-cache",
  redirect: "follow"
};
async function fetchBasic(url, fetchOptions = {}, fetchFn = fetch) {
  try {
    const response = await fetchFn(url, fetchOptions);
    if (response.status < 400) {
      return response;
    }
    try {
      log4.warn(`Fetch of ${url} with ${fetchOptions} returned status=${response.status}`);
      log4.warn(`Response: ${await response.text()}`);
    } catch (err) {
      log4.error("Exception:", err);
    }
  } catch (err) {
    log4.error("fetchBasic", err);
  }
}
__name(fetchBasic, "fetchBasic");
async function fetchJson(url, opts = {}, fetchFn = fetch) {
  try {
    let res = await fetchBasic(url, __spreadValues(__spreadProps(__spreadValues({
      method: "GET"
    }, defaultOptions), {
      headers: {}
    }), opts), fetchFn);
    if (res) {
      return await res.json();
    }
  } catch (err) {
    log4.error("fetchJSON error:", err);
  }
}
__name(fetchJson, "fetchJson");
function fetchOptionsFormURLEncoded(data, method = "POST") {
  return __spreadProps(__spreadValues({
    method
  }, defaultOptions), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=utf-8"
    },
    body: encodeQuery(data)
  });
}
__name(fetchOptionsFormURLEncoded, "fetchOptionsFormURLEncoded");
function fetchOptionsJson(data, method = "POST") {
  return __spreadProps(__spreadValues({
    method
  }, defaultOptions), {
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Accept: "application/json"
    },
    body: JSON.stringify(data)
  });
}
__name(fetchOptionsJson, "fetchOptionsJson");
async function fetchText(url, opts = {}, fetchFn = fetch) {
  try {
    let res = await fetchBasic(url, __spreadValues(__spreadProps(__spreadValues({
      method: "GET"
    }, defaultOptions), {
      headers: {}
    }), opts), fetchFn);
    if (res) {
      return await res.text();
    }
  } catch (err) {
    log4.error("fetchHTML error:", err);
  }
}
__name(fetchText, "fetchText");

// src/common/platform.ts
function getWindow() {
  if (typeof window !== "undefined")
    return window;
}
__name(getWindow, "getWindow");
function getNavigator() {
  if (typeof navigator !== "undefined")
    return navigator;
}
__name(getNavigator, "getNavigator");
function getGlobal() {
  var _a;
  return ((_a = getWindow()) != null ? _a : typeof WorkerGlobalScope !== "undefined") ? self : typeof global !== "undefined" ? global : Function("return this;")();
}
__name(getGlobal, "getGlobal");
var _navigator = getNavigator();
var _window = getWindow();
function detect(info = {
  ios: false,
  macos: false,
  windows: false,
  beaker: false,
  electron: false,
  wkwebview: false,
  pwa: false,
  pwaInstalled: false,
  browser: false,
  node: false,
  worker: false,
  jest: false,
  macosNative: false,
  iosNative: false,
  appleNative: false,
  touch: false
}) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  info.ios = ((_a = _navigator == null ? void 0 : _navigator.platform) == null ? void 0 : _a.match(/(iPhone|iPod|iPad)/i)) != null;
  info.macos = !!((_b = _navigator == null ? void 0 : _navigator.platform) == null ? void 0 : _b.startsWith("Mac"));
  info.windows = !!((_c = _navigator == null ? void 0 : _navigator.platform) == null ? void 0 : _c.startsWith("Win"));
  info.beaker = (_window == null ? void 0 : _window["beaker"]) != null;
  info.electron = (((_e = (_d = _navigator == null ? void 0 : _navigator.userAgent) == null ? void 0 : _d.toLowerCase()) == null ? void 0 : _e.indexOf(" electron/")) || -1) > -1 && !info.beaker;
  info.wkwebview = ((_f = _window == null ? void 0 : _window.webkit) == null ? void 0 : _f["messageHandlers"]) != null;
  info.pwa = (_navigator == null ? void 0 : _navigator.serviceWorker) != null;
  info.pwaInstalled = (_navigator == null ? void 0 : _navigator.standalone) || ((_h = (_g = _window == null ? void 0 : _window.matchMedia) == null ? void 0 : _g.call(_window, "(display-mode: standalone)")) == null ? void 0 : _h.matches);
  info.node = typeof process !== "undefined" && ((_i = process == null ? void 0 : process.release) == null ? void 0 : _i.name) === "node";
  info.browser = !info.electron && !info.wkwebview && !info.node;
  info.worker = typeof WorkerGlobalScope !== "undefined" && self instanceof WorkerGlobalScope;
  info.jest = typeof jest !== "undefined";
  info.macosNative = info.wkwebview && info.macos;
  info.iosNative = info.wkwebview && info.ios;
  info.appleNative = info.wkwebview;
  info.touch = _window && "ontouchstart" in _window || ((_navigator == null ? void 0 : _navigator.maxTouchPoints) || 0) > 1 || (_navigator == null ? void 0 : _navigator.msPointerEnabled) && (_window == null ? void 0 : _window.MSGesture) || (_window == null ? void 0 : _window.DocumentTouch) && document instanceof DocumentTouch;
  return info;
}
__name(detect, "detect");
var isBrowser = /* @__PURE__ */ __name(() => typeof window !== "undefined" && globalThis === window, "isBrowser");
var platform = detect();
function useExitHandler(handler) {
  if (isBrowser()) {
    window.addEventListener("beforeunload", handler);
  } else if (typeof process !== "undefined") {
    process.on("exit", () => handler);
  }
}
__name(useExitHandler, "useExitHandler");

// src/common/pool.ts
function usePool(config = {}) {
  const { maxParallel = 3 } = config;
  let currentParallel = 0;
  let priority = 0;
  let tasks = {};
  function performNext() {
    if (currentParallel >= maxParallel)
      return;
    let waitingTasks = Object.values(tasks).filter((t) => !t.running);
    if (waitingTasks.length > 0) {
      let taskInfo;
      for (let t of waitingTasks) {
        if (taskInfo == null || t.priority < taskInfo.priority) {
          taskInfo = t;
        }
      }
      if (taskInfo) {
        taskInfo.running = true;
        ++currentParallel;
        taskInfo.task().then((r) => {
          if (taskInfo == null ? void 0 : taskInfo.id)
            delete tasks[taskInfo.id];
          --currentParallel;
          performNext();
        }).catch((err) => {
          if (taskInfo == null ? void 0 : taskInfo.id)
            delete tasks[taskInfo.id];
          --currentParallel;
          performNext();
        });
      }
    }
  }
  __name(performNext, "performNext");
  function cancel(id) {
    let taskInfo = tasks[id];
    if (taskInfo && taskInfo.running !== true) {
      delete tasks[id];
    }
  }
  __name(cancel, "cancel");
  return {
    cancel,
    enqueue(id, task) {
      if (tasks[id] == null) {
        tasks[id] = {
          id,
          task,
          priority: ++priority,
          running: false
        };
        performNext();
      }
      return () => cancel(id);
    }
  };
}
__name(usePool, "usePool");

// src/common/queue.ts
var log5 = Logger("zeed:queue");
var SerialQueue = class {
  constructor(opt = {}) {
    this.queue = [];
    this.isPaused = false;
    this.waitToFinish = [];
    const { name = uname("queue"), logLevel } = opt;
    this.name = name;
    this.log = Logger(`zeed:queue:${name}`);
    this.log.level = logLevel != null ? logLevel : Infinity /* off */;
  }
  async performNext() {
    this.log(`performNext, queue.length =`, this.queue.length);
    if (this.currentTask != null) {
      this.log(`performNext => skip while another task is running`);
      return;
    }
    if (this.isPaused) {
      this.log(`performNext => skip while is paused`);
      return;
    }
    while (this.currentTask == null && !this.isPaused) {
      let info = this.queue.shift();
      this.log(`performNext => ${info == null ? void 0 : info.name}`);
      if (info == null) {
        break;
      }
      const { name, task, resolve } = info;
      this.currentTask = task();
      let result = void 0;
      try {
        this.log.info(`start task ${name}`);
        result = await this.currentTask;
        this.log(`finished task ${name} with result =`, result);
      } catch (err) {
        log5.warn("Error performing task", err);
      }
      resolve(result);
      this.currentTask = void 0;
    }
    while (this.waitToFinish.length > 0) {
      this.waitToFinish.shift()();
    }
  }
  async enqueue(task, opt = {}) {
    const { immediate: immediate2 = false, name = uname(this.name) } = opt;
    if (immediate2) {
      this.log.info(`immediate execution ${name}`);
      return await task();
    }
    this.log(`enqueue ${name}`);
    return new Promise((resolve) => {
      this.queue.push({
        name,
        task,
        resolve
      });
      this.performNext();
    });
  }
  async enqueueReentrant(task, opt = {}) {
    return this.enqueue(task, {
      immediate: this.currentTask != null,
      name: opt.name
    });
  }
  async cancelAll(unblock = true) {
    this.log(`cancelAll`);
    let resolver = this.queue.map((task) => task.resolve);
    this.queue = [];
    resolver.forEach((r) => r(void 0));
    await this.wait();
  }
  async pause() {
    this.log(`pause`);
    this.isPaused = true;
    await this.wait();
  }
  resume() {
    this.log(`resume`);
    this.isPaused = false;
    this.performNext();
  }
  async wait() {
    this.log(`wait`);
    if (this.currentTask == null && (this.queue.length === 0 || this.isPaused)) {
      return;
    }
    return new Promise((resolve) => {
      this.waitToFinish.push(resolve);
    });
  }
};
__name(SerialQueue, "SerialQueue");

// src/common/storage/memstorage.ts
var log6 = Logger("zeed:memstorage");
var MemStorage = class {
  constructor(opt = {}) {
    this.store = {};
  }
  setItem(key, value) {
    this.store[key] = cloneObject(value);
  }
  getItem(key) {
    if (this.store.hasOwnProperty(key)) {
      return cloneObject(this.store[key]);
    }
  }
  removeItem(key) {
    delete this.store[key];
  }
  clear() {
    this.store = {};
  }
  allKeys() {
    return Object.keys(this.store);
  }
};
__name(MemStorage, "MemStorage");

// src/common/throttle-debounce.ts
var DEBUG = false;
var log7 = DEBUG ? Logger("zeed:throttle") : () => {
};
function throttle(callback, opt = {}) {
  const { delay = 100, trailing = true, leading = true } = opt;
  let timeoutID = 0;
  let checkpoint = 0;
  let visited = 0;
  let debugCheckpoint = Date.now();
  function clearExistingTimeout() {
    if (timeoutID) {
      clearTimeout(timeoutID);
      timeoutID = void 0;
    }
  }
  __name(clearExistingTimeout, "clearExistingTimeout");
  function wrapper(...arguments_) {
    const now = Date.now();
    let self2 = this;
    let elapsed = now - checkpoint;
    function debugElapsed() {
      const dnow = Date.now();
      return `total ${(dnow - debugCheckpoint).toFixed(1)}ms - elapsed ${(dnow - checkpoint).toFixed(1)}ms - visited ${visited}x`;
    }
    __name(debugElapsed, "debugElapsed");
    function exec() {
      visited = 0;
      checkpoint = Date.now();
      callback.apply(self2, arguments_);
    }
    __name(exec, "exec");
    if (elapsed > delay || !timeoutID) {
      DEBUG && log7("elapsed", debugElapsed());
      if (leading) {
        if (elapsed > delay) {
          DEBUG && log7("\u{1F680} leading", debugElapsed());
          exec();
        } else {
          ++visited;
        }
      }
      const timeout2 = elapsed > delay ? delay : delay - elapsed;
      log7(`\u23F1 start timeout with ${timeout2.toFixed(1)}ms}`, debugElapsed());
      clearExistingTimeout();
      checkpoint = now;
      timeoutID = setTimeout(() => {
        DEBUG && log7("\u23F1 reached timeout", debugElapsed());
        timeoutID = 0;
        if (trailing && (!leading || visited > 0)) {
          DEBUG && log7("\u{1F680} trailing", debugElapsed());
          exec();
        }
      }, timeout2);
    } else {
      ++visited;
      DEBUG && log7("visited", debugElapsed());
    }
  }
  __name(wrapper, "wrapper");
  wrapper.cancel = clearExistingTimeout;
  wrapper.dispose = clearExistingTimeout;
  return wrapper;
}
__name(throttle, "throttle");
function debounce(callback, opt = {}) {
  const { delay = 100 } = opt;
  let timeoutID = 0;
  function clearExistingTimeout() {
    if (timeoutID) {
      clearTimeout(timeoutID);
      timeoutID = 0;
    }
  }
  __name(clearExistingTimeout, "clearExistingTimeout");
  function wrapper(...arguments_) {
    let self2 = this;
    clearExistingTimeout();
    timeoutID = setTimeout(() => {
      timeoutID = 0;
      callback.apply(self2, arguments_);
    }, delay);
  }
  __name(wrapper, "wrapper");
  wrapper.cancel = clearExistingTimeout;
  wrapper.dispose = clearExistingTimeout;
  return wrapper;
}
__name(debounce, "debounce");

export {
  _encodeUtf8Polyfill,
  _decodeUtf8Polyfill,
  stringToUInt8Array,
  Uint8ArrayToString,
  toUint8Array,
  toHex,
  toBase64,
  toBase64Url,
  fromBase64,
  equalBinary,
  jsonToUint8Array,
  Uint8ArrayToJson,
  randomUint8Array,
  digest,
  deriveKeyPbkdf2,
  encrypt,
  decrypt,
  csv,
  parseOrderby,
  composeOrderby,
  cmp,
  sortedOrderby,
  arrayUnique,
  arrayMinus,
  arrayUnion,
  arrayFlatten,
  arrayIntersection,
  arraySymmetricDifference,
  arrayRemoveElement,
  arraySetElement,
  arrayFilterInPlace,
  arrayToggleInPlace,
  arrayEmptyInPlace,
  arraySorted,
  arraySortedNumbers,
  arrayIsEqual,
  arrayShuffleInPlace,
  arrayShuffle,
  arrayShuffleForce,
  arrayRandomElement,
  arrayMax,
  arrayMin,
  createArray,
  useBase,
  encodeBase16,
  decodeBase16,
  encodeBase32,
  decodeBase32,
  encodeBase58,
  decodeBase58,
  encodeBase62,
  decodeBase62,
  encodeBase64,
  decodeBase64,
  toCamelCase,
  toCapitalize,
  toCapitalizeWords,
  fromCamelCase,
  jsonStringify,
  stringToBoolean,
  stringToInteger,
  stringToFloat,
  valueToBoolean,
  valueToInteger,
  valueToFloat,
  valueToString,
  toFloat,
  toInt,
  toString,
  toBool,
  formatMessages,
  renderMessages,
  fixBrokenUth8String,
  currency,
  Currency,
  isHalf,
  isEven,
  roundUp,
  roundDown,
  roundHalfUp,
  roundHalfOdd,
  roundHalfAwayFromZero,
  roundHalfDown,
  roundHalfEven,
  roundHalfTowardsZero,
  sleep,
  immediate,
  timeoutReached,
  timeout,
  timoutError,
  isTimeout,
  tryTimeout,
  waitOn,
  isPromise,
  promisify,
  DAY_MS,
  Day,
  forEachDay,
  today,
  day,
  escapeHTML,
  unescapeHTML,
  randomBoolean,
  randomInt,
  randomFloat,
  between,
  toValidFilename,
  escapeRegExp,
  startSortWeight,
  endSortWeight,
  moveSortWeight,
  sortedItems,
  linkifyPlainText,
  toHumanReadableUrl,
  encodeQuery,
  parseQuery,
  ensureKey,
  ensureKeyAsync,
  size,
  last,
  empty,
  cloneObject,
  cloneJsonObject,
  useDispose,
  useDisposer,
  useDefer,
  useTimeout,
  useInterval,
  useEventListener,
  isLocalHost,
  uuidBytes,
  uuid32bit,
  uuid,
  uuidEncode,
  uuidDecode,
  uuidB32,
  uname,
  qid,
  uuidv4,
  suidBytes,
  suid,
  suidDate,
  suidBytesDate,
  Emitter,
  getGlobalEmitter,
  messages,
  lazyListener,
  Channel,
  LocalChannel,
  fakeWorkerPair,
  NoopEncoder,
  JsonEncoder,
  CryptoEncoder,
  createPromiseProxy,
  useMessageHub,
  PubSub,
  usePubSub,
  useMutex,
  useAsyncMutex,
  fetchBasic,
  fetchJson,
  fetchOptionsFormURLEncoded,
  fetchOptionsJson,
  fetchText,
  getWindow,
  getNavigator,
  getGlobal,
  detect,
  isBrowser,
  platform,
  useExitHandler,
  usePool,
  SerialQueue,
  MemStorage,
  throttle,
  debounce
};
//# sourceMappingURL=chunk-SLALP6PY.js.map